import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";

import { injectChipIcons } from "../chipIcons";
import { Brand, SiteFooter } from "../components/brand";
import { caseStudies, caseStudyImageSizes } from "../caseStudies";
import type { CaseStudyCard } from "../caseStudies";
import { BOOK_CALL_URL, defaultLandingConfig, services } from "../landingData";
import { useBriefTracking, useScrollReveal } from "../landingClient";
import type {
  BeforeAfterRow,
  LandingConfig,
  PublicProfile,
} from "../server/landingTypes";
import { canonicalLink, jsonLd, seoMeta, SITE_URL } from "../seo";

const LANDING_IMAGE_VARIANTS: Record<string, Array<{ src: string; width: number }>> = {
  "/assets/case-study/fh/01.webp": [
    { src: "/assets/case-study/fh/01-320.webp", width: 320 },
    { src: "/assets/case-study/fh/01-640.webp", width: 640 },
    { src: "/assets/case-study/fh/01-1000.webp", width: 1000 },
    { src: "/assets/case-study/fh/01-1400.webp", width: 1400 },
  ],
  "/assets/case-study/1mind/01.webp": [
    { src: "/assets/case-study/1mind/01-320.webp", width: 320 },
    { src: "/assets/case-study/1mind/01-640.webp", width: 640 },
    { src: "/assets/case-study/1mind/01-1000.webp", width: 1000 },
    { src: "/assets/case-study/1mind/01-1400.webp", width: 1400 },
  ],
  "/assets/case-study/peaque/01.jpg": [
    { src: "/assets/case-study/peaque/01-320.webp", width: 320 },
    { src: "/assets/case-study/peaque/01-640.webp", width: 640 },
    { src: "/assets/case-study/peaque/01-1000.webp", width: 1000 },
    { src: "/assets/case-study/peaque/01-1400.webp", width: 1400 },
  ],
};

/** Stagger delay for [data-reveal] elements (consumed by the CSS reveal rules). */
const rvDelay = (seconds: number): CSSProperties =>
  ({ "--rvd": `${seconds}s` }) as CSSProperties;

export const Route = createFileRoute("/")({
  head: () => ({
    meta: seoMeta({
      title: "ThroughPilot - Senior Product Engineering for AI-era builds",
      description:
        "Senior product engineering that takes Lovable, Replit, and AI-era product builds through to production: auth, payments, data, security, and scale.",
      path: "/",
    }),
    links: [canonicalLink("/")],
    scripts: [
      jsonLd({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${SITE_URL}/#home`,
        url: SITE_URL,
        name: "ThroughPilot",
        description:
          "Senior product engineering that takes AI-era product builds through to production.",
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${SITE_URL}/#organization` },
      }),
      jsonLd({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Production Engineering for AI-era product builds",
        provider: { "@id": `${SITE_URL}/#organization` },
        serviceType: "Full-stack Product Engineering",
        areaServed: "Worldwide",
      }),
    ],
  }),
  component: LandingPage,
});

function LandingPage() {
  return <ThroughPilotLanding />;
}

export function ThroughPilotLanding({
  profile,
}: {
  profile?: PublicProfile | null;
}) {
  // Only the hero tech-stack chips are personalized today; everything else
  // (hero copy, before/after, case-study order, CTA) keeps the defaults.
  const config: LandingConfig = {
    ...defaultLandingConfig,
    ...(profile?.config ?? {}),
  };
  const orderedCaseStudies = config.caseStudyOrder.map((id) => caseStudies[id]);

  useEffect(() => {
    document.body.classList.add("landing");
    return () => document.body.classList.remove("landing");
  }, []);

  useBriefTracking(profile ?? null);
  useScrollReveal();

  useEffect(() => {
    injectChipIcons(document);
  }, [profile]);

  return (
    <>
      <main id="content">
      <div id="stage">
        <div id="hero" data-bg="glow">
          <div className="hero-bg" aria-hidden="true">
            <div className="bg-grid" />
            <div className="bg-dots" />
            <div className="bg-glow" />
          </div>

          <FlightPath />

          <nav>
            <Brand />
            <div className="navright">
              <div className="navlinks">
                <a href="#work">Work</a>
                <a href="#services">Services</a>
                <a href="#contact">Contact</a>
                <a href="/audit">Free Audit</a>
              </div>
              <BookCallLink className="btn pri">
                {config.ctaLabel} <span className="arr">↗</span>
              </BookCallLink>
            </div>
          </nav>

          <div className="body">
            <div className="left">
              <div className="kicker">
                <span className="sq" /> {config.heroKicker}
              </div>
              <HeroHeadline text={config.heroHeadline} />
              <p className="sub">{config.heroLead}</p>

              <div className="chips">
                {config.skills.map((skill, index) => (
                  <span
                    className={`chip${index < 2 ? " key" : ""}`}
                    key={`${skill}-${index}`}
                  >
                    {skill}
                  </span>
                ))}
              </div>

              <div className="ctas">
                <BookCallLink className="btn pri">
                  {config.ctaLabel} <span className="arr">↗</span>
                </BookCallLink>
                <a className="btn ghost audit-btn" href="/audit">
                  Get a Free Audit
                </a>
                <a className="btn ghost" href="#work">
                  View Case Studies
                </a>
              </div>

              {/* Mobile-only featured-work carousel — the visual right column
                  is hidden on small screens, so this keeps something to show
                  (and swipe) in the hero instead of a wall of text. */}
              <div className="hero-feature">
                <div className="blk-lbl hf-lbl">
                  <span>Featured work</span>
                  <span className="hf-hint">swipe ⇄</span>
                </div>
                <div className="hf-track">
                  {orderedCaseStudies.map((study) => (
                    <a className="hf-card" href={study.href} key={`hf-${study.id}`}>
                      <LandingCaseImage
                        fallbackWidth={640}
                        sizes="78vw"
                        study={study}
                      />
                      <div className="hf-meta">
                        <span className="hf-name">
                          {displayTitle(study)} <span className="ar">↗</span>
                        </span>
                        <span className="hf-desc">{study.miniDescription}</span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div className="leftfoot">
                <div className="noagency">
                  A senior product engineering practice.{" "}
                  <b>Hands-on, end to end, no hand-offs.</b>
                </div>
                <div className="proof">
                  <div className="pm">
                    <StatNumber value="57" />
                    <span className="l">projects shipped</span>
                  </div>
                  <div className="pm">
                    <StatNumber value="4,900+" />
                    <span className="l">hours delivered</span>
                  </div>
                  <div className="pm">
                    <StatNumber value="10+" />
                    <span className="l">years shipping</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="right">
              <div className="showcase">
                <div className="sc-head">
                  <div className="sc-title">
                    <b>{config.serviceFit}</b>
                    <span>before → after</span>
                  </div>
                  <div className="sc-drag">
                    drag to reveal <span>⇄</span>
                  </div>
                </div>
                <BeforeAfterSlider rows={config.beforeAfter} />
                <div className="sc-caption">
                  <p>
                    The bigger your app gets, the more the AI loses the thread,
                    and gaps like these quietly creep in. Each one gets hardened
                    and taken to production.
                  </p>
                </div>
              </div>

              <div className="lower">
                <div className="fwork">
                  <div className="blk-lbl">Featured work</div>
                  {orderedCaseStudies.map((study) => (
                    <a
                      className="fitem"
                      href={study.href}
                      key={`mini-${study.id}`}
                    >
                      {study.thumb ?? (
                        <LandingCaseImage
                          fallbackWidth={320}
                          sizes="104px"
                          study={study}
                        />
                      )}
                      <div>
                        <div className="fn">
                          {displayTitle(study)} <span className="ar">↗</span>
                        </div>
                        <div className="fd">{study.miniDescription}</div>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="svcs">
                  <div className="blk-lbl">Services</div>
                  {services.map((service) => (
                    <div className="svc" key={service.id}>
                      <div>
                        <div className="sn">{service.id}</div>
                        <div className="sd">{service.title}</div>
                      </div>
                      <ServiceTiming className="st" service={service} accent />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <TickerStrip />
        </div>
      </div>

      <section className="section" id="work">
        <div className="sec-kicker" data-reveal>
          <span className="sq" /> Selected work
        </div>
        <h2 className="sec-title" data-reveal style={rvDelay(0.08)}>
          Apps taken <em>through</em> to production.
        </h2>
        <p className="sec-intro" data-reveal style={rvDelay(0.16)}>
          Builds that started as ideas and shipped as real products, handling
          real money, real data, and real users.
        </p>

        {orderedCaseStudies.map((study, index) => (
          <div
            className={`case${index % 2 === 1 ? " flip" : ""}`}
            key={study.id}
          >
            <a className="case-media" href={study.href} data-reveal>
              <LandingCaseImage
                fallbackWidth={1000}
                sizes="(max-width: 820px) calc(100vw - 40px), 50vw"
                study={study}
              />
            </a>
            <div className="case-meta" data-reveal style={rvDelay(0.12)}>
              <div className="case-num">{study.caseNum}</div>
              <h3>{displayTitle(study)}</h3>
              <p className="lead">{study.lead}</p>
              <div className="tags">
                {study.tags.map((tag) => (
                  <span
                    className={`chip${tag.key ? " key" : ""}`}
                    key={tag.label}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
              <div className="out">
                {study.outcomes.map((outcome) => (
                  <div key={outcome}>{outcome}</div>
                ))}
              </div>
              <a className="btn ghost cta" href={study.href}>
                View Case Study <span className="arr">↗</span>
              </a>
            </div>
          </div>
        ))}
      </section>

      <section className="section" id="services">
        <div className="sec-kicker" data-reveal>
          <span className="sq" /> How it works
        </div>
        <h2 className="sec-title" data-reveal style={rvDelay(0.08)}>
          Three ways to <em>work</em> together.
        </h2>
        <p className="sec-intro" data-reveal style={rvDelay(0.16)}>
          Pick the entry point that matches where your product is today. Every
          engagement runs hands-on with a senior engineer - from the first
          call to the final deploy.
        </p>

        <div className="svc-grid" id="approach">
          {services.map((service, index) => (
            <div
              className={`svc-card${service.id === config.serviceFit ? " best-fit" : ""}`}
              data-reveal
              key={service.id}
              style={rvDelay(index * 0.1)}
            >
              <div className="tag">{service.tag}</div>
              <h3>{service.id}</h3>
              <div className="sub2">{service.title}</div>
              <ul>
                {service.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
              <ServiceTiming className="when" service={service} />
            </div>
          ))}
        </div>
      </section>

      <section className="section" id="contact">
        <div className="contact-inner">
          <div
            className="sec-kicker"
            data-reveal
            style={{ justifyContent: "center" }}
          >
            <span className="sq" /> Get in touch
          </div>
          <h2 className="contact-big" data-reveal style={rvDelay(0.08)}>
            Let's take it <em>through</em>
            <br />
            to production.
          </h2>
          <p className="contact-sub" data-reveal style={rvDelay(0.16)}>
            Say where your product is stuck. Get a straight answer on exactly
            what it takes to ship it - from the engineer who'll build it.
          </p>
          <div className="contact-ctas" data-reveal style={rvDelay(0.24)}>
            <BookCallLink className="btn pri">
              {config.ctaLabel} <span className="arr">↗</span>
            </BookCallLink>
            <a className="btn ghost" href="mailto:hello@throughpilot.com">
              hello@throughpilot.com
            </a>
          </div>
          <p className="contact-audit" data-reveal style={rvDelay(0.28)}>
            Not sure what's broken?{" "}
            <a href="/audit">Start with a free production-readiness audit</a> →
          </p>
          <div className="contact-proof" data-reveal style={rvDelay(0.32)}>
            <div className="pm">
              <StatNumber value="57" />
              <span className="l">projects shipped</span>
            </div>
            <div className="pm">
              <StatNumber value="4,900+" />
              <span className="l">hours delivered</span>
            </div>
            <div className="pm">
              <StatNumber value="10+" />
              <span className="l">years shipping</span>
            </div>
          </div>
        </div>
      </section>

      </main>
      <SiteFooter />
    </>
  );
}

// Everything that gets hardened on the way to production — scrolled as a
// ticker at the hero/work seam. Mirrors the before/after rows plus the rest
// of the production checklist.
const TICKER_ITEMS = [
  "Auth & access",
  "Payments",
  "Subscriptions",
  "Webhooks",
  "Data integrity",
  "Migrations",
  "Secrets & security",
  "Rate limiting",
  "Performance",
  "Monitoring",
  "CI/CD & deploys",
  "Scale",
];

function TickerStrip() {
  // Identical tracks scroll side by side; when one finishes its -100% travel
  // the next is exactly where it started, so the loop is seamless. Four copies
  // (~7600px) keep the strip gap-free up to 5K-wide viewports — one track is
  // only ~1900px. The copy is decorative (it repeats section content), so the
  // whole strip is hidden from assistive tech.
  const track = (
    <div className="tk-track">
      {TICKER_ITEMS.map((item) => (
        <span className="tk-item" key={item}>
          <span className="tk-sq" />
          {item}
        </span>
      ))}
    </div>
  );
  return (
    <div className="ticker" aria-hidden="true">
      {track}
      {track}
      {track}
      {track}
    </div>
  );
}

// Flight routes for the hero, in each SVG's viewBox space. The same `d` string
// drives the drawn line, its reveal mask, and the plane's offset-path, so the
// three can never drift apart.
const FP_DESKTOP_D =
  "M -60 150 C 260 40, 430 340, 730 360 C 1010 378, 1190 210, 1500 250";
const FP_MOBILE_D = "M -20 170 C 70 40, 230 180, 420 60";

/**
 * Decorative "flight route" behind the hero: a dashed path that draws itself
 * on load while the brand paper plane flies it, then keeps a slow dash-flow.
 * Separate desktop/mobile artboards (toggled in CSS) so the curve can be
 * composed for each layout instead of stretching one path across both.
 */
function FlightPath() {
  return (
    <div className="flightpath" aria-hidden="true">
      <FlightPathArt
        className="fp-desktop"
        d={FP_DESKTOP_D}
        maskId="fp-reveal-d"
        planeScale={1.2}
        viewBox="0 0 1440 620"
      />
      <FlightPathArt
        className="fp-mobile"
        d={FP_MOBILE_D}
        maskId="fp-reveal-m"
        planeScale={1.1}
        viewBox="0 0 400 200"
      />
    </div>
  );
}

function FlightPathArt({
  className,
  d,
  maskId,
  planeScale,
  viewBox,
}: {
  className: string;
  d: string;
  maskId: string;
  planeScale: number;
  viewBox: string;
}) {
  return (
    <svg className={className} viewBox={viewBox}>
      <defs>
        <mask id={maskId}>
          <path className="fp-mask" d={d} pathLength={1} />
        </mask>
      </defs>
      <path className="fp-line" d={d} mask={`url(#${maskId})`} pathLength={1} />
      <g className="fp-plane" style={{ offsetPath: `path("${d}")` }}>
        {/* Simplified LogoMark silhouette, nose pointing +x for offset-rotate */}
        <g transform={`scale(${planeScale})`}>
          <path d="M 12 0 L -8 -5 L -2 0 Z" />
          <path d="M 12 0 L -2 0 L -5 7 Z" opacity={0.55} />
        </g>
      </g>
    </svg>
  );
}

/**
 * Proof metric that counts up from zero the first time it scrolls into view.
 * Server-renders (and keeps, for reduced-motion / no-JS) the final value, so
 * the real number is always in the HTML.
 */
function StatNumber({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    const match = /^([\d,]+)(.*)$/.exec(value);
    if (!el || !match) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const target = Number(match[1].replace(/,/g, ""));
    const suffix = match[2];
    let raf = 0;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now: number) => {
          const p = Math.min(1, (now - start) / 2000);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = `${Math.round(target * eased).toLocaleString("en-US")}${suffix}`;
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.6 },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value]);

  return (
    <span ref={ref} className="n">
      {value}
    </span>
  );
}

function BookCallLink({
  children,
  className,
}: {
  children: ReactNode;
  className: string;
}) {
  return (
    <a
      className={className}
      href={BOOK_CALL_URL}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </a>
  );
}

function LandingCaseImage({
  fallbackWidth,
  sizes,
  study,
}: {
  fallbackWidth: number;
  sizes: string;
  study: CaseStudyCard;
}) {
  const dimensions = caseStudyImageSizes[study.image];
  const variants = LANDING_IMAGE_VARIANTS[study.image];
  const fallback =
    variants?.find((variant) => variant.width >= fallbackWidth)?.src ??
    variants?.at(-1)?.src ??
    study.image;

  return (
    <img
      src={fallback}
      srcSet={variants
        ?.map((variant) => `${variant.src} ${variant.width}w`)
        .join(", ")}
      sizes={variants ? sizes : undefined}
      alt={study.imageAlt}
      decoding="async"
      height={dimensions?.height}
      loading="lazy"
      width={dimensions?.width}
    />
  );
}

function ServiceTiming({
  accent = false,
  className,
  service,
}: {
  accent?: boolean;
  className: string;
  service: (typeof services)[number];
}) {
  return (
    <div className={className}>
      {service.when.prefix ? `${service.when.prefix} ` : ""}
      <b className={accent ? "accent" : undefined}>{service.when.emphasis}</b>
    </div>
  );
}

function HeroHeadline({ text }: { text: string }) {
  // The brand's signature word "through" is set in serif italic (see `.h1 em`),
  // broken onto its own line — matching the design handoff and the contact heading.
  const marker = "through";
  const idx = text.toLowerCase().indexOf(marker);
  if (idx !== -1) {
    const before = text.slice(0, idx).trimEnd();
    const word = text.slice(idx, idx + marker.length);
    const after = text.slice(idx + marker.length);
    return (
      <h1 className="h1">
        {before}
        <br />
        <em>{word}</em>
        {after}
      </h1>
    );
  }

  // Fallback for other headlines: emphasize a trailing second sentence if present.
  const parts = text.split(/(?<=\.)\s+/);
  if (parts.length < 2) {
    return <h1 className="h1">{text}</h1>;
  }

  return (
    <h1 className="h1">
      {parts[0]}
      <br />
      <em>{parts.slice(1).join(" ")}</em>
    </h1>
  );
}

/** Title with the live domain's TLD appended where available (e.g. "1Mind" → "1Mind.com"). */
function displayTitle(study: CaseStudyCard): string {
  if (!study.domain) return study.title;
  const tld = study.domain.slice(study.domain.lastIndexOf("."));
  return study.title.toLowerCase().endsWith(tld)
    ? study.title
    : study.title + tld;
}

function BeforeAfterSlider({ rows }: { rows: Array<BeforeAfterRow> }) {
  const baRef = useRef<HTMLDivElement>(null);
  const beforeRef = useRef<HTMLDivElement>(null);
  const beforeMockRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const gripRef = useRef<HTMLSpanElement>(null);
  const tagAfterRef = useRef<HTMLSpanElement>(null);
  const tagBeforeRef = useRef<HTMLSpanElement>(null);
  const isDragging = useRef(false);

  function baLayout(px?: number) {
    const ba = baRef.current;
    const before = beforeRef.current;
    const beforeMock = beforeMockRef.current;
    const handle = handleRef.current;
    const tagAfter = tagAfterRef.current;
    const tagBefore = tagBeforeRef.current;
    if (!ba || !before || !beforeMock || !handle) return;

    const w = ba.clientWidth;
    if (!w) return;
    const nextPx = Math.max(
      0,
      Math.min(w, px ?? (before.offsetWidth || w * 0.5)),
    );

    before.style.width = `${nextPx}px`;
    beforeMock.style.width = `${w}px`;
    handle.style.left = `${nextPx}px`;

    const f = nextPx / w;
    if (tagAfter)
      tagAfter.style.opacity =
        f > 0.86 ? String(Math.max(0, (1 - f) / 0.14)) : "1";
    if (tagBefore)
      tagBefore.style.opacity = f < 0.14 ? String(Math.max(0, f / 0.14)) : "1";
  }

  function baFrom(event: PointerEvent) {
    const ba = baRef.current;
    if (!ba) return 0;
    const rect = ba.getBoundingClientRect();
    const scale = rect.width / ba.clientWidth || 1;
    return (event.clientX - rect.left) / scale;
  }

  useEffect(() => {
    const ba = baRef.current;
    const grip = gripRef.current;
    if (!ba) return;

    // Once the visitor grabs the handle themselves, the auto-sweep stays off.
    let interacted = false;

    const onDown = (event: PointerEvent) => {
      isDragging.current = true;
      interacted = true;
      grip?.classList.remove("nudge");
      try {
        ba.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture can fail in embedded browser contexts.
      }
      baLayout(baFrom(event));
      event.preventDefault();
    };
    const onMove = (event: PointerEvent) => {
      if (isDragging.current) baLayout(baFrom(event));
    };
    const onUp = () => {
      isDragging.current = false;
    };
    const onResize = () => baLayout();

    ba.addEventListener("pointerdown", onDown);
    ba.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("resize", onResize);
    baLayout();

    // Auto-sweep demo: once the hero has settled, the handle glides to the
    // "before" side, holds, glides to "after", and returns to center — telling
    // the broken→shipped story without a drag. It repeats every so often, and
    // the first pointerdown (which sets `interacted`) retires it for good.
    let sweepRaf = 0;
    let sweepTimer = 0;
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // from/to are fractions of the card width (larger = more "before").
      const legs = [
        { from: 0.5, to: 0.88, dur: 950 },
        { from: 0.88, to: 0.88, dur: 450 },
        { from: 0.88, to: 0.14, dur: 1250 },
        { from: 0.14, to: 0.14, dur: 450 },
        { from: 0.14, to: 0.5, dur: 850 },
      ];
      const easeInOut = (p: number) =>
        p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;

      const startSweep = () => {
        if (interacted || !ba.clientWidth) return;
        grip?.classList.remove("nudge");
        let leg = 0;
        let legStart = performance.now();
        const tick = (now: number) => {
          if (interacted) return;
          const { from, to, dur } = legs[leg];
          const p = Math.min(1, (now - legStart) / dur);
          baLayout(ba.clientWidth * (from + (to - from) * easeInOut(p)));
          if (p >= 1) {
            leg += 1;
            legStart = now;
            if (leg >= legs.length) {
              grip?.classList.add("nudge");
              sweepTimer = window.setTimeout(startSweep, 10000);
              return;
            }
          }
          sweepRaf = requestAnimationFrame(tick);
        };
        sweepRaf = requestAnimationFrame(tick);
      };
      sweepTimer = window.setTimeout(startSweep, 1700);
    }

    return () => {
      ba.removeEventListener("pointerdown", onDown);
      ba.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("resize", onResize);
      window.clearTimeout(sweepTimer);
      cancelAnimationFrame(sweepRaf);
    };
  }, [rows]);

  return (
    <div ref={baRef} className="ba">
      <div className="ba-after">
        <div className="mock">
          <div className="arch-h">
            <span>Production readiness</span>
            <span className="score">● shipped</span>
          </div>
          {rows.map((row) => (
            <ReadinessRow
              detail={row.after}
              key={row.key}
              label={row.label}
              status="✓"
            />
          ))}
        </div>
        <span ref={tagAfterRef} className="ba-tag" style={{ right: "9px" }}>
          after · production-ready
        </span>
      </div>

      <div ref={beforeRef} className="ba-before">
        <div ref={beforeMockRef} className="mock broken">
          <div className="arch-h">
            <span>Production readiness</span>
            <span className="score">○ failing</span>
          </div>
          {rows.map((row) => (
            <ReadinessRow
              detail={row.before}
              key={row.key}
              label={row.label}
              status="✕"
            />
          ))}
        </div>
        <span ref={tagBeforeRef} className="ba-tag" style={{ left: "9px" }}>
          before · as it grew
        </span>
      </div>

      <div ref={handleRef} className="ba-handle">
        <span ref={gripRef} className="ba-grip nudge">
          ⇄
        </span>
      </div>
    </div>
  );
}

function ReadinessRow({
  detail,
  label,
  status,
}: {
  detail: string;
  label: string;
  status: string;
}) {
  return (
    <div className="crow">
      <div className="cic">
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M2.5 8.2h11" />
          <path d="M8 2.7v11" />
        </svg>
      </div>
      <div className="cmain">
        <div className="ct">{label}</div>
        <div className="cd">{detail}</div>
      </div>
      <div className="cstat">{status}</div>
    </div>
  );
}
